import React, { useEffect, useState } from "react";
import {
  Button,
  Popover,
  List,
  ListItem,
  ListItemButton,
  Checkbox,
  TextField,
  InputAdornment,
  CircularProgress,
  Box,
  Typography,
  Stack,
  IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import { OrganisationFileResponse } from "../../models/OrganisationModels";
import { useOrganisationFiles } from "../../hooks/useOrganisationFiles";
import { useDebouncedValue } from "../../hooks/useDebounceValue";
import { useTaskpaneTelemetry } from "../../telemetry/useTaskpaneTelemetry";

type OrganisationFilePickerProps = {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onAdd: (files: OrganisationFileResponse[]) => void;
  /** When provided, picker opens with these pre-selected and user can deselect; onAdd receives full current selection. */
  initialSelected?: OrganisationFileResponse[];
  /** When initialSelected is not used, these ids are shown as checked and disabled (add-only mode). */
  alreadySelectedIds?: string[];
};

export function OrganisationFilePicker({
  open,
  anchorEl,
  onClose,
  onAdd,
  initialSelected,
  alreadySelectedIds = [],
}: OrganisationFilePickerProps) {
  const { trackReceivableOrganisationAttachmentAdded } = useTaskpaneTelemetry();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useDebouncedValue("", 400);
  const [selectedFiles, setSelectedFiles] = useState<OrganisationFileResponse[]>([]);
  const { files, isLoading } = useOrganisationFiles({
    searchTerm: debouncedSearchTerm || undefined,
    pageNumber: 1,
    pageSize: 50,
    enabled: open,
  });

  useEffect(() => {
    if (open) {
      setSearchTerm("");
      setDebouncedSearchTerm("");
      setSelectedFiles(initialSelected ?? []);
    }
  }, [open, setDebouncedSearchTerm, initialSelected]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setDebouncedSearchTerm(e.target.value);
  };

  const toggle = (file: OrganisationFileResponse) => {
    setSelectedFiles((prev) => {
      const idx = prev.findIndex((f) => f.id === file.id);
      if (idx >= 0) {
        const next = [...prev];
        next.splice(idx, 1);
        return next;
      }
      return [...prev, file];
    });
  };

  const isItemSelected = (id: string) => selectedFiles.some((f) => f.id === id);
  const isDisabled = (id: string) => initialSelected === undefined && alreadySelectedIds.includes(id);

  const handleAdd = () => {
    let addedCount = 0;
    if (initialSelected !== undefined) {
      addedCount = selectedFiles.length;
      onAdd(selectedFiles);
    } else {
      const toAdd = selectedFiles.filter((f) => !alreadySelectedIds.includes(f.id));
      addedCount = toAdd.length;
      if (toAdd.length) onAdd(toAdd);
    }
    if (addedCount > 0) {
      trackReceivableOrganisationAttachmentAdded({ file_count: String(addedCount) });
    }
    onClose();
  };

  const displayName = (f: OrganisationFileResponse) =>
    f.displayName || f.fileName || `File ${f.fileExtension || ""}`.trim() || "Unnamed";

  return (
    <Popover
      open={open && !!anchorEl}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      transformOrigin={{ vertical: "bottom", horizontal: "right" }}
      PaperProps={{
        elevation: 8,
        sx: {
          width: 320,
          maxHeight: 400,
          mb: 1,
          overflow: "hidden",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2), 0 4px 12px rgba(0, 0, 0, 0.15)",
        },
      }}
    >
      <Stack sx={{ p: 0 }} spacing={0}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: "divider" }}
        >
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Attach files</Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            {selectedFiles.length > 0 && (
              <Button
                size="small"
                onClick={() => setSelectedFiles([])}
                sx={{ textTransform: "none", fontSize: 12, minWidth: "auto", py: 0 }}
              >
                Clear ({selectedFiles.length})
              </Button>
            )}
            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>
        <Box sx={{ px: 2, pt: 1.5, pb: 0 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search files..."
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 1.5 }}
          />
        </Box>
        <Box sx={{ px: 0, minHeight: 160, maxHeight: 220, overflow: "auto" }}>
          {isLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={120}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <List dense disablePadding sx={{ py: 0, px: 2 }}>
              {files.map((file) => {
                const checked = isItemSelected(file.id);
                const disabled = isDisabled(file.id);
                return (
                  <ListItem key={file.id} disablePadding>
                    <ListItemButton
                      onClick={() => !disabled && toggle(file)}
                      disabled={disabled}
                      dense
                      sx={{ py: 0.5, px: 0 }}
                    >
                      <Checkbox
                        edge="start"
                        checked={checked || disabled}
                        disabled={disabled}
                        tabIndex={-1}
                        size="small"
                        sx={{ py: 0.25, pr: 1 }}
                      />
                      <Box sx={{ minWidth: 0, flex: 1, overflow: "hidden" }}>
                        <Typography variant="body2" noWrap title={displayName(file)}>
                          {displayName(file)}
                        </Typography>
                        {file.description && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                            display="block"
                            title={file.description}
                          >
                            {file.description}
                          </Typography>
                        )}
                      </Box>
                    </ListItemButton>
                  </ListItem>
                );
              })}
              {!isLoading && files.length === 0 && (
                <ListItem>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    {debouncedSearchTerm ? "No files match your search." : "No organisation files found."}
                  </Typography>
                </ListItem>
              )}
            </List>
          )}
        </Box>
        <Stack
          direction="row"
          justifyContent="flex-end"
          spacing={1}
          sx={{ px: 2, py: 1.5, pt: 0.5, borderTop: 1, borderColor: "divider" }}
        >
          <Button onClick={onClose} size="small">
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleAdd}
            disabled={initialSelected === undefined && selectedFiles.length === 0}
            startIcon={selectedFiles.length > 0 ? <AttachFileIcon sx={{ fontSize: 18 }} /> : undefined}
          >
            {selectedFiles.length === 0 ? "Select none" : `Add (${selectedFiles.length})`}
          </Button>
        </Stack>
      </Stack>
    </Popover>
  );
}
